import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserData } from "../context/userContext";
import { FaLocationDot } from "react-icons/fa6";
import { LoaderData } from "../context/loaderContext";
import Loader from "../components/Loader/Loader";
import { sessions, workshopsReach } from "../constants/workshops";
import { AMBASSADOR_REGISTRATION_CLOSED } from "../constants/events";
import "../styles/profile.css";
import axios from "axios";

const Profile = () => {
  const { profile, user, userEvents, session } = UserData();
  const navigate = useNavigate();
  const { isLoading } = LoaderData();

  const [referralCode, setReferralCode] = useState(null); // store referral code
  const [isRegistering, setIsRegistering] = useState(false);
  const [loadingReferral, setLoadingReferral] = useState(true); // loading state for referral check

  const fallbackUser = {
    id: "10001",
    name: "Guest",
    email: "guest@cse.com",
    mobile: "123456789",
    abacusId: "999",
    college: "College of Engineering Guindy, Anna University",
    year: "4",
    dept: "CSE",
    registeredEvents: [1, 2, 3],
    registeredWorkshops: [1, 2, 3],
  };

  const userData = user || fallbackUser;

  const navigateTo = (page) => {
    const routes = {
      events: "/events",
      workshops: "/workshops",
      update: "/profile/update",
      dashboard: "/dashboard",
      "change-password": "/profile/change-password",
    };
    navigate(routes[page]);
  };

  // ✅ Check referral code on mount
  useEffect(() => {
    const checkReferral = async () => {
      // Only check if we have a real user with valid auth
      const token = localStorage.getItem("abacustoken");
      if (!user || !user.id || !user.email || !token || user.id === "10001") {
        setLoadingReferral(false);
        return;
      }

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/user/get-my-referral-code`,
          { email: user.email },
          { headers: { token } },
        );

        if (res.data.hasCode) {
          setReferralCode(res.data.referralCode);
        } else {
          setReferralCode(null);
        }
      } catch (err) {
        console.error("Referral check failed", err);
        setReferralCode(null);
      } finally {
        setLoadingReferral(false);
      }
    };

    checkReferral();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Only re-run when user ID changes (not on every user property update)

  // ✅ Handle registration
  const handleRegisterCA = async () => {
    const token = localStorage.getItem("abacustoken");

    if (!user || !user.abacusId || !user.id || user.id === "10001") {
      alert("User data not loaded. Please refresh the page.");
      return;
    }

    if (!token) {
      alert("Authentication token missing. Please log in again.");
      navigate("/auth");
      return;
    }

    setIsRegistering(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/user/register-ca-from-user`,
        { abacusId: user.abacusId },
        { headers: { token } },
      );
      setReferralCode(res.data.campusAmbassador.referralCode);
    } catch (err) {
      console.error("Registration error:", err);
      alert(err.response?.data?.message || "Error registering CA");
    } finally {
      setIsRegistering(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="user-profile-container">
      <div className="user-card">
        <div className="user-header">
          <h2>Profile</h2>
          <div className="user-info-bar">
            <div className="info-text sa-text mb-2">
              <p>
                <b>Student Ambassadors</b> who achieve <b>25 event</b>{" "}
                registrations will receive a <b>Selection Certificate</b>{" "}
                endorsed by CSEA, CEG – Anna University. Those who secure{" "}
                <b>5 workshop</b> registrations will get{" "}
                <b>free access to one tech workshop</b> at ABACUS’26. The
                best-performing Student Ambassador will be rewarded with{" "}
                <b>exclusive merchandise and exciting goodies</b>.
              </p>{" "}
            </div>

            {/* 1️⃣ Show button or referral code based on state */}
            {loadingReferral ? (
              <p>Checking ambassador status...</p>
            ) : referralCode ? (
              <p className="referral-code">
                Your Referral Code: <strong>{referralCode}</strong>
              </p>
            ) : AMBASSADOR_REGISTRATION_CLOSED ? (
              <div className="bg-red-500/20 border-2 border-red-500 text-red-400 font-semibold py-3 px-6 rounded-lg text-center m-5">
                Ambassador Registration Closed
              </div>
            ) : (
              <button
                className="action-btn m-5 ambassador"
                onClick={handleRegisterCA}
                disabled={isRegistering}
              >
                {isRegistering
                  ? "Registering..."
                  : "Register as Student Ambassador"}
              </button>
            )}
          </div>
          <div className="user-details-grid">
            <p>
              <strong>Name:</strong> {userData.name}
            </p>
            <p>
              <strong>ID:</strong> {userData.id}
            </p>
            <p>
              <strong>Email:</strong> {userData.email}
            </p>
            <p>
              <strong>Mobile:</strong> {userData.mobile}
            </p>
          </div>
        </div>

        {/* Content Section: Events & Workshops */}
        <div className="user-content">
          {/* Registered Events */}
          <div className="user-section events">
            <h3>Registered Events</h3>
            {user.events?.length > 0 ? (
              <ul>
                {userEvents.map((event, index) => (
                  <li key={index}>{event.eventName}</li>
                ))}
              </ul>
            ) : (
              <p>
                <button
                  onClick={() => navigateTo("events")}
                  className="hover:text-slate-100"
                >
                  Click here to Register for Events
                </button>
              </p>
            )}
          </div>

          {/* Registered Workshops */}
          <div className="user-section workshops">
            <h3>Registered Workshops</h3>
            {user.WorkshopPayment?.length > 0 ? (
              <ul>
                {user.WorkshopPayment.map((workshop, index) => {
                  const matchingWorkshop = workshopsReach.find(
                    (ws) =>
                      ws.code === workshop.workshopId &&
                      workshop.status === "SUCCESS",
                  );
                  return matchingWorkshop ? (
                    <li key={`success-${index}`} className="status-success">
                      {matchingWorkshop.title}
                    </li>
                  ) : null;
                })}

                {user.WorkshopPayment.map((workshop, index) => {
                  const matchingWorkshop = workshopsReach.find(
                    (ws) =>
                      ws.code === workshop.workshopId &&
                      workshop.status === "PENDING",
                  );
                  return matchingWorkshop ? (
                    <li key={`pending-${index}`} className="status-pending">
                      {matchingWorkshop.title} (Pending)
                    </li>
                  ) : null;
                })}

                {session.length > 0 &&
                  user.workshops.map((workshop, index) => {
                    const matchingWorkshop = sessions.find(
                      (ws) => ws.code === workshop.workshopId,
                    );
                    return matchingWorkshop ? (
                      <li key={`session-${index}`}>{matchingWorkshop.title}</li>
                    ) : null;
                  })}
              </ul>
            ) : (
              <p>
                <button
                  onClick={() => navigateTo("workshops")}
                  className="hover:text-slate-100"
                >
                  Click here to Register for Workshops
                </button>
              </p>
            )}
          </div>
        </div>

        {/* Info Bar / Location */}
        <div className="user-info-bar">
          <div className="info-text">
            <FaLocationDot className="icon-location" />
            <span>
              Studying in {userData.college}. <br />
              Striding through year {userData.year} in the {userData.dept}{" "}
              department!
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="user-actions">
          {[
            {
              label: "Update Profile",
              page: "update",
              className: "btn-update",
            },
            {
              label: "Dashboard",
              page: "dashboard",
              className: "btn-dashboard",
            },
            {
              label: "Change Password",
              page: "change-password",
              className: "btn-profile-password",
            },
          ].map(({ label, page, onClick, className }) => (
            <button
              key={label}
              className={`action-btn ${className}`}
              onClick={onClick || (() => navigateTo(page))}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Profile;
